import React, { useState } from 'react';
import { Box, AppBar, Toolbar, Typography, IconButton, Drawer, List, ListItem, ListItemIcon, ListItemText, Avatar, Menu, MenuItem, Divider, Tooltip } from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Group,
  BarChart,
  Description,
  Person,
  Logout,
  DarkMode,
  LightMode,
  Search,
  ConfirmationNumber,
  CalendarMonth
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import NotificationBell from './NotificationBell';
import GlobalSearch from './GlobalSearch';
import ProfileDialog from './ProfileDialog';
import { isManagerRole, ROLE_LABELS } from '../../constants/domain';
import { avatarUrl } from '../../utils/avatar';
import solidproLogo from '../../assets/solidpro-logo.svg';

const drawerWidth = 260;

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { darkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);

  const menuItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/' },
    // Head/Manager/Admin only -- engineers don't get this item (or the route).
    ...(isManagerRole(user?.role)
      ? [{ text: 'Team Dashboard', icon: <Group />, path: '/team-dashboard' }]
      : []),
    { text: 'Parts Tracker', icon: <Search />, path: '/parts' },
    { text: 'JIRA Tickets', icon: <ConfirmationNumber />, path: '/tickets' },
    { text: 'Calendar', icon: <CalendarMonth />, path: '/calendar' },
    // Head/Manager/Admin only -- engineers don't get these items (or the routes).
    ...(isManagerRole(user?.role)
      ? [
        { text: 'Charts', icon: <BarChart />, path: '/charts' },
        { text: 'Reports', icon: <Description />, path: '/reports' },
      ]
      : []),
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
    handleMenuClose();
  };

  const handleNavigate = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ justifyContent: 'center', bgcolor: 'background.paper', py: 1 }}>
        <Box component="img" src={solidproLogo} alt="Solidpro" sx={{ height: 32 }} />
      </Toolbar>
      <Box sx={{
        mx: 2, mb: 1.5, p: 2, textAlign: 'center', borderRadius: 3, color: '#fff',
        backgroundImage: 'linear-gradient(135deg, #112a68 0%, #1b3f94 55%, #3f68c9 100%)',
        boxShadow: '0 8px 20px rgba(27,63,148,0.28)',
      }}>
        <Avatar src={avatarUrl(user?.avatar_path)}
          sx={{
            width: 60, height: 60, mx: 'auto', mb: 1, bgcolor: 'secondary.main',
            border: '3px solid rgba(255,255,255,0.85)', fontWeight: 700,
          }}>
          {user?.full_name?.charAt(0) || 'U'}
        </Avatar>
        <Typography variant="subtitle1" fontWeight="bold" noWrap>
          {user?.full_name || 'User'}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.85 }}>
          {ROLE_LABELS[user?.role] || user?.role || 'Engineer'} • {user?.department || 'Engineering'}
        </Typography>
      </Box>
      <List sx={{ flex: 1, px: 1.5 }}>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            onClick={() => handleNavigate(item.path)}
            selected={location.pathname === item.path}
            sx={{
              borderRadius: 2.5, mb: 0.5, py: 1,
              transition: 'background-color .2s ease, transform .2s ease',
              '&:hover': { transform: 'translateX(3px)' },
              '& .MuiListItemIcon-root': { minWidth: 40 },
              '&.Mui-selected': {
                backgroundImage: 'linear-gradient(135deg, #1b3f94, #3f68c9)',
                color: 'primary.contrastText',
                boxShadow: '0 6px 14px rgba(27,63,148,0.32)',
                '&:hover': {
                  backgroundImage: 'linear-gradient(135deg, #112a68, #1b3f94)',
                },
                '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
                '& .MuiListItemText-primary': { fontWeight: 600 },
              },
            }}
          >
            <ListItemIcon>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem button onClick={toggleTheme}>
          <ListItemIcon>{darkMode ? <LightMode /> : <DarkMode />}</ListItemIcon>
          <ListItemText primary={darkMode ? 'Light Mode' : 'Dark Mode'} />
        </ListItem>
        <ListItem button onClick={handleLogout}>
          <ListItemIcon><Logout /></ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          boxShadow: 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {menuItems.find(item => item.path === location.pathname)?.text || 'Dashboard'}
          </Typography>
          <GlobalSearch />
          <NotificationBell />
          <Tooltip title="Profile">
            <IconButton
              color="inherit"
              onClick={handleMenuOpen}
              sx={{ ml: 1 }}
            >
              <Avatar src={avatarUrl(user?.avatar_path)}
                sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                {user?.full_name?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={() => { handleMenuClose(); setProfileOpen(true); }}>
              <ListItemIcon>
                <Person fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={() => { handleMenuClose(); toggleTheme(); }}>
              <ListItemIcon>
                {darkMode ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
              </ListItemIcon>
              {darkMode ? 'Light Mode' : 'Dark Mode'}
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box', width: drawerWidth,
              borderRight: 'none', boxShadow: '4px 0 24px rgba(27,63,148,0.08)',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default',
          mt: '64px',
        }}
      >
        {children}
      </Box>
      <ProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} />
    </Box>
  );
};

export default Layout;